import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { DotsHorizontal } from "untitledui-js-base";

import { ReactElement, ReactNode, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { cx } from "shared/utils/classnames";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { Card } from "shared/components/Card";
import { Placement } from "@floating-ui/react";
import { getTriggerClassName } from "./utils";

export type Action = {
  title: ReactNode;
  onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void | unknown;
  color?: string;
  disabled?: boolean;
  tooltip?: string;
  tooltipPlacement?: Placement;
  className?: string;
};

const Trigger = ({
  open,
  triggerClassName,
  trigger,
  cbTrigger,
  borderLess
}: {
  open: boolean;
  triggerClassName?: string;
  trigger?: ReactElement;
  cbTrigger?: (open: boolean) => ReactElement;
  borderLess: boolean;
}): ReactElement => {
  if (trigger) return trigger;

  if (cbTrigger) return cbTrigger(open);

  return (
    <IconButton
      borderLess={borderLess}
      variant={IconButtonVariant.BASE}
      size={IconButtonSize.SM}
      className={cx(
        "block bg-transparent stroke-content-1000",
        triggerClassName
      )}
      Icon={DotsHorizontal}
    />
  );
};

export const ContextMenuPopover = ({
  actions,
  triggerClassName,
  contentClassName,
  cardClassName,
  contentBorderless = false,
  trigger,
  cbTrigger,
  disabled,
  borderLess = true,
  placement,
  disableFlip,
  maxHeight
}: {
  actions: Action[];
  triggerClassName?: string;
  contentClassName?: string;
  cardClassName?: string;
  contentBorderless?: boolean;
  borderLess?: boolean;
  trigger?: ReactElement;
  cbTrigger?: (open: boolean) => ReactElement;
  disabled?: boolean;
  placement?: Placement;
  disableFlip?: boolean;
  maxHeight?: number;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement={placement}
      disableFlip={disableFlip}
      maxHeight={maxHeight}
    >
      <PopoverTrigger
        disabled={disabled}
        onClick={(e) => {
          if (disabled) return;
          e.stopPropagation();
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <Trigger
          open={open}
          triggerClassName={triggerClassName}
          trigger={trigger}
          cbTrigger={cbTrigger}
          borderLess={borderLess}
        />
      </PopoverTrigger>
      <PopoverContent className={cx("Popover z-50", contentClassName)}>
        <div data-click-outside-ignore>
          <Card
            className={cx(
              "min-w-[270px]",
              contentBorderless && "border-0",
              cardClassName
            )}
          >
            {actions.map((action, idx) => {
              return (
                <div
                  key={
                    typeof action.title === "string" ? action.title : "" + idx
                  }
                  className="w-full"
                >
                  <Tooltip placement={action.tooltipPlacement}>
                    <TooltipTrigger
                      key={
                        typeof action.title === "string"
                          ? action.title
                          : "" + idx
                      }
                      className={cx(
                        "w-full cursor-pointer border-b border-alpha-40 p-2 px-4 text-left text-base font-medium",
                        getTriggerClassName({
                          idx,
                          actionsLength: actions.length,
                          disabled: action.disabled
                        }),
                        action.color || "text-content-1000",
                        action.className
                      )}
                      as="div"
                      onClick={(e) => {
                        e.stopPropagation();
                        !action.disabled &&
                          Promise.resolve(
                            action.onClick(
                              e as React.MouseEvent<HTMLDivElement, MouseEvent>
                            )
                          ).finally(() => {
                            setOpen(false);
                          });
                      }}
                    >
                      {action.title}
                    </TooltipTrigger>

                    {action.disabled && action.tooltip ? (
                      <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-4 text-sm text-white z-[51]">
                        {action.tooltip}
                      </TooltipContent>
                    ) : null}
                  </Tooltip>
                </div>
              );
            })}
          </Card>
        </div>
      </PopoverContent>
    </Popover>
  );
};
